import { css } from 'styled-components'

/**
 * The card shared by every screen outside the session: sign in, sign up and recover password.
 * They all take the same height so the card does not jump when moving from one to the next;
 * the value is the sign in card's, the tallest content of the three.
 */
export const AUTH_CARD_HEIGHT = 700

export const authCard = css`
  width: 100%;
  max-width: 384px;
  min-height: ${ AUTH_CARD_HEIGHT }px;
  flex: 0 0 auto;
  border-radius: 28px;
  box-shadow: 0 12px 40px 0 rgba(0, 0, 0, 0.25);
  padding: 40px 32px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  background-color: ${ ({ theme }) => theme.colors.white };

  @media screen and (max-width: 768px) {
    /* A phone is shorter than the card: let the content decide there. */
    min-height: auto;
    padding: 32px 24px 24px;
  }
`

// Logo and title at the top of the card, the same on every screen: centred, no rule underneath.
export const authHeader = css`
  header {
    flex: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 40px;

    img.logoAmb {
      max-width: 260px;
    }

    img.logoNemiRounded,
    img.logoBeiraBaixaRounded {
      width: 96px;
      height: 96px;
    }

    h2 {
      margin: 20px 0 0;
      font-size: 24px;
      font-weight: bold;
      color: ${ ({ theme }) => theme.colors.primary };
    }
  }
`

// The screen behind the card: centres it in the viewport, with the language pill pinned to the
// top right corner. On a phone the card is taller than the screen, so instead of centring (which
// clips the top) the screen grows with the card and scrolls, leaving room for the pill above it.
export const authScreen = css`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 25px;
  box-sizing: border-box;

  .language-selector {
    position: absolute;
    top: 25px;
    right: 25px;
    z-index: 10;
  }

  @media screen and (max-width: 768px) {
    height: auto;
    min-height: 100%;
    justify-content: flex-start;
    padding: 72px 16px 24px;

    .language-selector {
      top: 16px;
      right: 16px;
    }
  }
`

// A hairline with a few words in the middle ("or with your account", "or else").
export const authDivider = css`
  .divider {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    margin: 24px 0;
    color: ${ ({ theme }) => theme.colors.black };
    font-size: 12px;
    white-space: nowrap;

    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background-color: ${ ({ theme }) => theme.colors.greyLight };
    }
  }
`

// The parts these screens' forms share (links, actions, errors), so the same markup looks the
// same wherever it is hosted — including the guest prompt.
export const authForm = css`
  ${ authDivider }

  .link {
    text-align: center;

    span {
      color: ${ ({ theme }) => theme.colors.primary };
      font-size: 16px;
      font-weight: 500;
      letter-spacing: 0;
      line-height: 15px;
      text-align: center;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  /* The button sits at the bottom of the shared card height whatever the step's field count. */
  .actions {
    margin-top: auto;
    padding-top: 32px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;

    button {
      height: 52px;
      font-size: 16px;
      letter-spacing: 1px;

    }

    .link {
      margin-top: 30px;
    }
  }

  /* While signing in / sending: the spinner takes the button's place, centred and just as tall. */
  .actions .loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 52px;
  }

  .error-container {
    flex-direction: row;
    justify-content: center;
    margin-top: 25px;
    width: 100%;
    text-align: center;
    align-self: center;

    .error {
      color: ${ ({ theme }) => theme.colors.red };
      text-align: center;
    }
  }
`
